import type { TFunction, TOptions } from 'i18next';

/**
 * Type to remove count suffix from a string.
 */
export type RemoveCount<T> = T extends `${infer P}_${'one' | 'few' | 'many' | 'other'}` ? P : T;

type GetByPath<T, K> = K extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? GetByPath<T[Head], Rest>
    : unknown
  : K extends keyof T
    ? T[K]
    : unknown;

/**
 * Leaf key paths of a translation subtree. Falls back to `string` when the
 * subtree cannot be resolved (generic / non-literal path).
 */
type FlattenKeys<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T & string]: T[K] extends Record<string, unknown> ? `${K}.${FlattenKeys<T[K]>}` : RemoveCount<K>;
      }[keyof T & string]
    : string;

/**
 * Type for a nested key in a translation without count suffix.
 */
export type TranslationNestedKey<TRoot, T extends string> = FlattenKeys<GetByPath<TRoot, T>>;

/**
 * Type returned from getTranslation
 */
export type TranslationResult<TRoot, T extends string> = (
  key: TranslationNestedKey<TRoot, T>,
  options?: TOptions,
) => string;

/**
 * Unscoped `t()`. Uses i18next's `TFunction` instead of a catalog-wide path
 * union — that constraint was the main tsc hotspot.
 */
export type CustomTFunction<_TRoot = unknown> = TFunction;

/**
 * Curried function to scope translations to a base path
 */
export const getTranslation =
  <TRoot, T extends string>(t: TFunction | CustomTFunction<TRoot>, base: T) =>
  (key: TranslationNestedKey<TRoot, T>, options: TOptions = {}) =>
    t([base, key].join('.'), options);
